/**
 * Экран согласия на геометки (6.5, 17.7).
 *
 * Это не юридическая формальность и не мелкий шрифт: няня должна увидеть
 * ровно четыре вещи — что фиксируется, когда, кто видит и как отключить.
 * «Не сейчас» — полноценный вариант: без согласия приложение работает так же,
 * просто без точек.
 */

import { useGrantGeoConsent } from '@/shared/api/day'
import { Button } from '@/shared/ui/Button'
import { Sheet } from '@/shared/ui/Sheet'
import { Icon } from '@/shared/ui/Icon'
import { ErrorBanner } from '@/shared/ui/states'
import { toast } from '@/shared/ui/toast'

const FACTS = [
  {
    icon: 'geo-tap' as const,
    title: 'Только когда вы нажимаете',
    text: 'Точка сохраняется в момент нажатия: «Начать смену», «Забрала», «Я на месте». Больше никогда.',
  },
  {
    icon: 'geo-no-track' as const,
    title: 'Никакого фонового отслеживания',
    text: 'Приложение не спрашивает вашу позицию в фоне и не может этого сделать — так устроен Telegram.',
  },
  {
    icon: 'geo-who-sees' as const,
    title: 'Видят только родители этой семьи',
    text: 'Никому больше точки не показываются и никуда не передаются.',
  },
  {
    icon: 'geo-revoke' as const,
    title: 'Отключается в один тап',
    text: 'В профиле, без объяснений. Родителю придёт нейтральное уведомление, и всё продолжит работать.',
  },
]

export function GeoConsentSheet({
  open,
  familyId,
  onClose,
}: {
  open: boolean
  familyId: number
  onClose: () => void
}) {
  const grant = useGrantGeoConsent(familyId, {
    onSuccess: () => {
      toast.success('Геометки включены')
      onClose()
    },
  })

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Геометки"
      footer={
        <div className="space-y-2">
          <Button
            block
            size="lg"
            loading={grant.isPending}
            hapticKind="success"
            onClick={() => grant.mutate()}
          >
            Разрешить
          </Button>
          <Button block size="lg" variant="ghost" onClick={onClose} hapticKind="none">
            Не сейчас
          </Button>
        </div>
      }
    >
      {grant.error && <ErrorBanner message={grant.error.message} />}

      <p className="text-text-muted text-body leading-snug">
        Родители включили геометки. Это добровольно — решаете вы.
      </p>

      <div className="mt-4 space-y-3">
        {FACTS.map((fact) => (
          <div key={fact.title} className="flex gap-3">
            <span
              aria-hidden
              className="bg-accent-soft flex size-9 shrink-0 items-center justify-center rounded-[var(--radius-thumb)]"
            >
              <Icon name={fact.icon} size={20} />
            </span>
            <div className="min-w-0">
              <p className="text-[15px] font-semibold">{fact.title}</p>
              <p className="text-text-muted text-caption leading-snug">{fact.text}</p>
            </div>
          </div>
        ))}
      </div>

      <p className="text-text-muted mt-4 text-caption leading-snug">
        После «Разрешить» телефон отдельно спросит доступ к геолокации — это обычный
        системный запрос, он появится при первом нажатии кнопки.
      </p>
    </Sheet>
  )
}
